/**
 * Career Domain Detail Page
 * Shows domain overview and specializations
 */

import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getDomainBySlug } from '../../api/career-api';
import type { CareerDomain } from '../../types';
import { Header } from '../../../../shared/components/Header';
import { Footer } from '../../../../shared/components/Footer';

const DomainDetailPage: React.FC = () => {
  console.log('🎯🎯 DomainDetailPage component function called');

  const params = useParams();
  console.log('🎯🎯 ALL URL params:', params);
  const domainSlug = params['domain-slug'];
  console.log('🎯🎯 domainSlug extracted:', domainSlug);

  const navigate = useNavigate();
  const [domain, setDomain] = useState<CareerDomain | null>(() => {
    console.log('🎯🎯 Initializing domain state to null');
    return null;
  });
  const [isLoading, setIsLoading] = useState(() => {
    console.log('🎯🎯 Initializing isLoading state to TRUE');
    return true;
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('🎯🎯 useEffect triggered - domainSlug:', domainSlug);
    if (domainSlug) {
      console.log('🎯🎯 domainSlug exists, calling loadDomain()');
      loadDomain();
    } else {
      console.log('🎯🎯 ❌ No domainSlug found!');
    }
  }, [domainSlug]);

  const loadDomain = async () => {
    console.log('🎯🎯 loadDomain() START - slug:', domainSlug);
    try {
      console.log('🎯🎯 Setting isLoading to TRUE');
      setIsLoading(true);
      console.log('🎯🎯 Setting error to null');
      setError(null);

      console.log('🎯🎯 About to call getDomainBySlug with:', domainSlug);
      const data = await getDomainBySlug(domainSlug!);
      console.log('🎯🎯 getDomainBySlug returned:', data);
      console.log('🎯🎯 Domain name:', data?.name);
      console.log('🎯🎯 Number of specializations:', data?.specializations?.length);

      console.log('🎯🎯 About to call setDomain()');
      setDomain(data);
      console.log('🎯🎯 setDomain() called');
    } catch (err: any) {
      console.error('🎯🎯 ❌ ERROR in loadDomain:', err);
      console.log('🎯🎯 Error message:', err.message);
      setError(err.message || 'Failed to load domain');
    } finally {
      console.log('🎯🎯 FINALLY block - setting isLoading to FALSE');
      setIsLoading(false);
      console.log('🎯🎯 loadDomain() COMPLETE');
    }
  };

  console.log('🎯🎯 RENDER - isLoading:', isLoading, '| domain:', domain?.name || 'null', '| error:', error);

  if (isLoading) {
    console.log('🎯🎯 ⏳ RENDERING LOADING SPINNER (isLoading is TRUE)');
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading domain...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  console.log('🎯🎯 ✅ Past loading check - rendering content or error');

  if (error || !domain) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">😕</div>
            <h2 className="text-2xl font-bold mb-3">Domain Not Found</h2>
            <p className="text-muted-foreground mb-6">
              {error || 'The domain you are looking for does not exist.'}
            </p>
            <Link
              to="/explore/careers"
              className="inline-block bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
            >
              Back to Explore Careers
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background">
      {/* Breadcrumb */}
      <div className="bg-muted/30 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/explore/careers" className="hover:text-primary transition-colors">
              Explore Careers
            </Link>
            <span>→</span>
            <span className="text-foreground font-medium">{domain.name}</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary via-primary to-emerald-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-6">
            <div className="text-7xl">{domain.icon_emoji || '📚'}</div>
            <div className="flex-1">
              <h1 className="text-4xl md:text-5xl font-extrabold mb-4">{domain.name}</h1>
              <p className="text-xl text-white/90 max-w-3xl">{domain.short_description}</p>

              {/* Key Stats */}
              <div className="flex flex-wrap gap-6 mt-6">
                {domain.avg_salary_usd && (
                  <div>
                    <div className="text-sm text-white/70 mb-1">Average Salary</div>
                    <div className="text-2xl font-bold">
                      ${(domain.avg_salary_usd / 1000).toFixed(0)}k/year
                    </div>
                  </div>
                )}
                {domain.job_growth_percent && (
                  <div>
                    <div className="text-sm text-white/70 mb-1">Job Growth</div>
                    <div className="text-2xl font-bold text-green-300">
                      +{domain.job_growth_percent}%
                    </div>
                  </div>
                )}
                {domain.total_jobs && (
                  <div>
                    <div className="text-sm text-white/70 mb-1">Total Jobs</div>
                    <div className="text-2xl font-bold">
                      {(domain.total_jobs / 1000).toFixed(0)}k+
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* What Is It Section */}
        {domain.what_is_it && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-4">What is {domain.name}?</h2>
            <p className="text-lg text-muted-foreground leading-relaxed">{domain.what_is_it}</p>
          </div>
        )}

        {/* Key Skills */}
        {domain.key_skills && Array.isArray(domain.key_skills) && domain.key_skills.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-4">Key Skills You'll Develop</h2>
            <div className="flex flex-wrap gap-3">
              {domain.key_skills.map((skill, index) => (
                <div
                  key={index}
                  className="bg-primary/10 text-primary px-4 py-2 rounded-lg font-medium"
                >
                  {skill}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Work Environment */}
        {domain.work_environment && (
          <div className="mb-12 bg-muted/30 rounded-2xl p-6">
            <h2 className="text-2xl font-bold mb-4">Work Environment</h2>
            <p className="text-muted-foreground leading-relaxed">{domain.work_environment}</p>
          </div>
        )}

        {/* Famous People */}
        {domain.famous_people && Array.isArray(domain.famous_people) && domain.famous_people.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-4">Notable Figures in This Field</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {domain.famous_people.map((person, index) => (
                <div key={index} className="bg-card border border-border rounded-xl p-4">
                  <div className="font-bold text-lg">{person.name}</div>
                  <div className="text-sm text-muted-foreground">{person.role}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Specializations */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-6">Explore Specializations</h2>
          <p className="text-muted-foreground text-lg mb-8">
            Choose a specialization to see detailed career paths, salaries, companies, and more.
          </p>

          {domain.specializations && domain.specializations.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {domain.specializations
                .sort((a, b) => a.display_order - b.display_order)
                .map((spec) => (
                  <Link
                    key={spec.id}
                    to={`/explore/careers/${domain.slug}/${spec.slug}`}
                    className="group bg-card border-2 border-border hover:border-primary rounded-2xl p-6 transition-all hover:shadow-lg"
                  >
                    {/* Icon & Name */}
                    <div className="flex items-start gap-4 mb-4">
                      <div className="text-4xl">{spec.icon_emoji || '💼'}</div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors">
                          {spec.name}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {spec.description}
                        </p>
                      </div>
                    </div>

                    {/* Salary Range */}
                    {spec.avg_salary_entry && spec.avg_salary_senior && (
                      <div className="bg-muted/50 rounded-xl p-4 mb-4">
                        <div className="text-xs text-muted-foreground mb-2">Salary Range</div>
                        <div className="font-bold text-primary">
                          ${(spec.avg_salary_entry / 1000).toFixed(0)}k - $
                          {(spec.avg_salary_senior / 1000).toFixed(0)}k
                        </div>
                      </div>
                    )}

                    {/* Badges */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {spec.demand_level && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-md font-medium">
                          {spec.demand_level} Demand
                        </span>
                      )}
                      {spec.job_outlook && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-md font-medium">
                          {spec.job_outlook} Outlook
                        </span>
                      )}
                      {spec.work_type && (
                        <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-md font-medium">
                          {spec.work_type}
                        </span>
                      )}
                    </div>

                    {/* Arrow */}
                    <div className="text-primary font-semibold text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                      View Details
                      <span className="text-lg">→</span>
                    </div>
                  </Link>
                ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <div className="text-6xl mb-4">🔍</div>
              <p className="text-lg">No specializations available yet.</p>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-br from-primary/10 to-emerald-50 border-2 border-primary rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold mb-3">Want Personalized Recommendations?</h3>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Take our quick personality quiz to find out which specialization matches your
            interests, work style, and career goals.
          </p>
          <Link
            to="/explore/careers/match"
            className="inline-block bg-gradient-to-r from-primary to-emerald-600 hover:from-primary/90 hover:to-emerald-700 text-white px-8 py-3 rounded-xl font-semibold transition-all shadow-md hover:shadow-lg"
          >
            Get My Match
          </Link>
        </div>
      </div>
      </div>
      <Footer />
    </>
  );
};

export default DomainDetailPage;
